'use strict';

const APPLICATION_ROLES = Object.freeze(['admin', 'org_admin', 'advisor', 'introducer', 'service-provider', 'introducer-manager', 'service-provider-manager', 'familly_member_parent', 'familly_member_kid']);
const ROLE_ALIASES = Object.freeze({
  'org-admin': 'org_admin', 'organization-admin': 'org_admin',
  'system-administrator': 'admin', 'system-administrators': 'admin', 'platform-admin': 'admin', 'platform-administrator': 'admin',
  adviser: 'advisor', manager: 'advisor', 'relationship-manager': 'advisor',
  'business-admin': 'advisor', 'business-administrator': 'advisor',
  'advisor-firm': 'advisor', 'adviser-firm': 'advisor', 'advisor-firm-admin': 'advisor', 'advisor-firm-administrator': 'advisor', 'practice-admin': 'advisor',
  'familly-member-parent': 'familly_member_parent', 'key-stakeholders-parents': 'familly_member_parent', 'key-stakeholders-parents-advisors': 'familly_member_parent', 'primary-stakeholder': 'familly_member_parent',
  'familly-member-kid': 'familly_member_kid', 'family-members': 'familly_member_kid', stakeholder: 'familly_member_kid',
});
const FAMILY_CONTENT_ACCESS_RULES = Object.freeze({
  'questionnaire.view': Object.freeze({ admin: 'permitted-family', org_admin: 'permitted-family', advisor: 'permitted-family', familly_member_parent: 'own-member', familly_member_kid: 'own-member' }),
  'business-profile.view': Object.freeze({ admin: 'permitted-family', org_admin: 'permitted-family', advisor: 'permitted-family', familly_member_parent: 'permitted-family', familly_member_kid: 'permitted-family' }),
  'questionnaire.edit': Object.freeze({ familly_member_parent: 'own-member', familly_member_kid: 'own-member' }),
  'questionnaire.completion.manage': Object.freeze({ admin: 'permitted-family', familly_member_parent: 'own-member', familly_member_kid: 'own-member' }),
  'business-profile.edit': Object.freeze({ familly_member_parent: 'permitted-family', familly_member_kid: 'permitted-family' }),
  'business-profile.import': Object.freeze({ admin: 'permitted-family', familly_member_parent: 'permitted-family', familly_member_kid: 'permitted-family' }),
  'forward-planning.responses.edit': Object.freeze({ familly_member_parent: 'own-member', familly_member_kid: 'own-member' }),
  'forward-planning.requests.manage': Object.freeze({ admin: 'permitted-family', org_admin: 'permitted-family', advisor: 'permitted-family' }),
});

function normalizeFamilyContentRole(role) {
  const value = String(role || '').trim().toLowerCase().replace(/\s+/g, '-');
  if (APPLICATION_ROLES.includes(value)) return value;
  const alias = value.replace(/_/g, '-');
  return Object.prototype.hasOwnProperty.call(ROLE_ALIASES, alias) ? ROLE_ALIASES[alias] : '';
}

function getFamilyContentAccess({ sessionActive = false, role, roles = [] } = {}) {
  const granted = new Set([role, ...(Array.isArray(roles) ? roles : [])].map(normalizeFamilyContentRole).filter(Boolean));
  const effectiveRole = sessionActive ? APPLICATION_ROLES.find(candidate => granted.has(candidate)) || '' : '';
  const scope = action => FAMILY_CONTENT_ACCESS_RULES[action]?.[effectiveRole] || null;
  return Object.freeze({
    canViewQuestionnaire: Boolean(scope('questionnaire.view')),
    canViewBusinessProfile: Boolean(scope('business-profile.view')),
    canEditQuestionnaire: Boolean(scope('questionnaire.edit')),
    canManageQuestionnaireCompletion: Boolean(scope('questionnaire.completion.manage')),
    canEditBusinessProfile: Boolean(scope('business-profile.edit')),
    canImportBusinessProfile: Boolean(scope('business-profile.import')),
    canEditForwardPlanningResponses: Boolean(scope('forward-planning.responses.edit')),
    canManageForwardPlanningRequests: Boolean(scope('forward-planning.requests.manage')),
    questionnaireReadScope: scope('questionnaire.view'),
    questionnaireEditScope: scope('questionnaire.edit'),
    questionnaireCompletionScope: scope('questionnaire.completion.manage'),
    businessProfileReadScope: scope('business-profile.view'),
    businessProfileEditScope: scope('business-profile.edit'),
    businessProfileImportScope: scope('business-profile.import'),
    forwardPlanningResponseEditScope: scope('forward-planning.responses.edit'),
    forwardPlanningRequestScope: scope('forward-planning.requests.manage'),
  });
}

function assertFamilyContentAccess(accessProfile, capability) {
  const access = getFamilyContentAccess(accessProfile);
  if (!Object.prototype.hasOwnProperty.call(access, capability) || access[capability] !== true) {
    const requests = capability === 'canManageForwardPlanningRequests';
    const businessProfileImport = capability === 'canImportBusinessProfile';
    throw Object.assign(new Error(requests
      ? 'Your access does not permit Forward Planning change requests.'
      : businessProfileImport
        ? 'Your access does not permit legacy Business Profile imports.'
        : 'This family content is available for review only.'), {
      statusCode: 403,
      code: requests
        ? 'FORWARD_PLANNING_REQUEST_ACCESS_DENIED'
        : businessProfileImport
          ? 'BUSINESS_PROFILE_IMPORT_ACCESS_DENIED'
          : 'FAMILY_CONTENT_READ_ONLY',
    });
  }
  return access;
}

module.exports = Object.freeze({ FAMILY_CONTENT_ACCESS_RULES, normalizeFamilyContentRole, getFamilyContentAccess, assertFamilyContentAccess });
